import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import _ from 'lodash';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import DoneIcon from '@material-ui/icons/CheckCircle';
import { createEditorState } from 'medium-draft';
import { FormattedMessage } from 'react-intl';

import Audio from '../../lessonDetails/Audio/Audio';
import s from './CoursePodcastItem.scss';
import isLoggedIn from '../../../helpers/checkAuth';
import LessonTextContent from '../../lessonDetails/Text/LessonTextContent';
import LessonReviewModal from '../../lessonDetails/LessonReviewModal/LessonReviewModal';
import * as lessonDetailsActions from '../../../actions/lessonDetails';
import * as courseDetailsActions from '../../../actions/courseDetails';
import * as podcastLessonActions from '../../../actions/podcastLesson';

import messages from '../../lessonDetails/lessonDetailsMessages';
import decorators from '../../lessonDetails/Text/decorators';
import convertImages from '../../lessonDetails/Text/convertImages';
import {
  convertRichEditorContentToHtml,
  getImagesFromDraftJS,
} from '../../../helpers';
import LessonSources from '../../lessonDetails/LessonSources/LessonSources';

@withStyles(s)
@connect(
  state => ({
    auth: state.auth,
    course: state.courseDetails.course,
    lesson: state.lessonDetails.lesson,
    courseWasReset: state.courseDetails.courseWasReset,
    playPodcastAudio: state.podcastLesson.playPodcastAudio,
    openedPodcastLesson: state.podcastLesson.openedPodcastLesson,
  }),
  dispatch =>
    bindActionCreators(
      {
        updateState: lessonDetailsActions.updateState,
        completeLesson: lessonDetailsActions.completeLesson,
        getCourseBySlug: courseDetailsActions.getCourseBySlug,
        resetLesson: lessonDetailsActions.reset,
        setPlayPodcastAudio: podcastLessonActions.setPlayPodcastAudio,
      },
      dispatch,
    ),
)
export default class CoursePodcastItem extends React.Component {
  static propTypes = {
    auth: PropTypes.object,  // eslint-disable-line
    course: PropTypes.object, // eslint-disable-line
    lesson:  PropTypes.object, // eslint-disable-line
    completeLesson: PropTypes.func.isRequired,
    getCourseBySlug: PropTypes.func.isRequired,
    courseWasReset: PropTypes.bool.isRequired,
    lessonItem: PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      time: PropTypes.number,
      isGloballyFree: PropTypes.bool,
    }).isRequired,
    resetLesson: PropTypes.func.isRequired,
    playPodcastAudio: PropTypes.bool,
    openedPodcastLesson: PropTypes.string,
    goToNextLesson: PropTypes.func,
    setPlayPodcastAudio: PropTypes.func.isRequired,
    isCompleted: PropTypes.bool.isRequired,
    cancelGoToNextLesson: PropTypes.func,
  };

  static defaultProps = {
    course: {},
    playPodcastAudio: false,
    openedPodcastLesson: '',
    goToNextLesson: _.noop,
    cancelGoToNextLesson: _.noop,
  };

  static contextTypes = {
    fetch: PropTypes.func,
    cloudfrontUrl: PropTypes.string,
  };

  constructor(props, context) {
    super(props);
    this.completeOnce = _.once(this.complete);

    if (!_.get(props, 'lesson._id')) {
      return;
    }
    const isContentHasBigImages = !!_.find(
      _.get(props, 'lesson.content.entityMap', []),
      elem =>
        _.get(elem, 'type') === 'IMAGE' &&
        (parseInt(_.get(elem, 'data.width', 0), 10) > 70 ||
          !_.includes(['right', 'left'], _.get(elem, 'data.alignment'))),
    );

    const newContent = convertImages({ cloudfrontUrl: context.cloudfrontUrl })(
      _.get(props, 'lesson.content'),
    );
    const newSources = _.cloneDeep(_.get(props, 'lesson.sources'));

    const editorState = createEditorState(newContent, decorators);

    const editorStateContent = editorState && editorState.getCurrentContent();
    const imageContent = getImagesFromDraftJS(_.get(props, 'lesson.content'));

    const isTextExist =
      (editorStateContent &&
        editorStateContent.hasText() &&
        !!editorStateContent.getPlainText().trim().length) ||
      !!_.get(imageContent, 'length');

    const convertedSourcesData = convertRichEditorContentToHtml(
      newSources,
      context.cloudfrontUrl,
    );

    this.state = {
      isContentHasBigImages,
      isTextExist,
      editorState,
      showLessonReviewModal: false,
      isAudioEnded: false,
      isSourcesExist: convertedSourcesData.isTextExist,
      sourcesHTML: convertedSourcesData.contentHTML,
    };
  }

  onEditorChange = (editorState, callback) => {
    this.setState({ editorState }, () => {
      if (callback) {
        callback();
      }
    });
  };

  setAudioEnded = isAudioEnded => {
    this.setState({ isAudioEnded });
  };

  complete = async () => {
    const {
      lesson: { _id: lessonId, slug: lessonSlug },
      course: { _id: courseId, slug: courseSlug },
      completeLesson,
      getCourseBySlug,
      auth,
    } = this.props;

    if (!courseId || !isLoggedIn(auth)) {
      return true;
    }

    await completeLesson({ lessonId, courseId, courseSlug });
    // need to update cache
    await this.context.fetch(
      `/api/lessons/by-slug/${lessonSlug}?courseId=${courseId}`,
    );
    await getCourseBySlug(courseSlug);
    return Promise.resolve();
  };

  showLessonReviewContent = () => {
    this.setState({
      showLessonReviewModal: true,
    });
    if (this.state.isAudioEnded) {
      this.props.cancelGoToNextLesson();
    } else {
      this.props.setPlayPodcastAudio(false);
    }
  };

  closeLessonReviewContent = () => {
    this.setState({
      showLessonReviewModal: false,
    });
    if (!this.state.isAudioEnded) this.props.setPlayPodcastAudio(true);
  };

  render() {
    const {
      lesson,
      courseWasReset,
      playPodcastAudio,
      openedPodcastLesson,
      lessonItem,
      goToNextLesson,
      setPlayPodcastAudio,
      auth,
      course,
      isCompleted,
    } = this.props;

    const {
      contentLength,
      isTextExist,
      isContentHasBigImages,
      showLessonReviewModal,
      isSourcesExist,
      sourcesHTML,
    } = this.state;

    const showContentInColumn = contentLength > 450 || isContentHasBigImages;

    return (
      <div
        className={classNames(s.lessonContainer, {
          [s.showContentInColumn]: showContentInColumn,
        })}
      >
        {!!_.get(lesson, 'audios.length') &&
          _.get(lesson, '_id') === _.get(lessonItem, '_id') &&
          _.get(lessonItem, '_id') === openedPodcastLesson && (
            <Audio
              key={`${lesson._id}${this.props.lessonItem._id}_audio_block_${courseWasReset}`}
              complete={this.completeOnce}
              podcastAudioContentClass={classNames(s.podcastAudioContent, {
                [s.showContentInColumn]: showContentInColumn,
              })}
              play={playPodcastAudio}
              setAudioEnded={this.setAudioEnded}
              goToNextLesson={goToNextLesson}
              setPlayPodcastAudio={setPlayPodcastAudio}
            />
          )}
        <div
          className={classNames(s.contentContainer, {
            [s.showContentInColumn]:
              showContentInColumn || !_.get(lesson, 'audios.length'),
          })}
        >
          <div
            className={classNames({
              [s.showContentInColumn]: showContentInColumn,
            })}
          >
            <LessonTextContent
              editorState={this.state.editorState}
              onEditorChange={this.onEditorChange}
              isTextExist={isTextExist}
              fullSize
            >
              <LessonSources
                headerTitle={<FormattedMessage {...messages.sourcesLabel} />}
                isSourcesExist={isSourcesExist}
                sourcesHTML={sourcesHTML}
              />
            </LessonTextContent>
          </div>
          {isLoggedIn(auth) && course._id && (
            <Button
              data-test-id="sL_uv9OEpYyVIWSZI73kn"
              variant="contained"
              color="primary"
              id="complete-lesson"
              className={classNames(
                s.tabletFullWidth,
                s.actionButtonWrapper,
                s.statusBtn,
                {
                  [s.completeButtonSuccess]: isCompleted || lesson.isCompleted,
                },
              )}
              onClick={
                isCompleted || lesson.isCompleted
                  ? this.showLessonReviewContent
                  : this.completeOnce
              }
              size="large"
            >
              {(lesson.isCompleted || isCompleted) && (
                <DoneIcon className={s.buttonIcon} />
              )}
              {lesson.isCompleted || isCompleted ? (
                <FormattedMessage {...messages.completedBtnMsg} />
              ) : (
                ''
              )}
              {!lesson.isCompleted && !isCompleted ? (
                <FormattedMessage {...messages.completeBtnmsg} />
              ) : (
                ''
              )}
            </Button>
          )}
        </div>
        {showLessonReviewModal && (
          <LessonReviewModal
            closeLessonReviewContent={this.closeLessonReviewContent}
          />
        )}
      </div>
    );
  }
}
